<script setup>
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAudioPlayback } from './useAudioPlayback';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
  compact: { type: Boolean, default: false },
});

defineOptions({
  inheritAttrs: false,
});

const timeStampURL = computed(() => {
  return timeStampAppendedURL(attachment.dataUrl);
});

const TRANSCRIPT_PREVIEW_LENGTH = 200;
const isTranscriptExpanded = ref(false);
const isTranscriptLong = computed(
  () => (attachment.transcribedText?.length || 0) > TRANSCRIPT_PREVIEW_LENGTH
);
const displayedTranscript = computed(() => {
  const text = attachment.transcribedText || '';
  if (!isTranscriptLong.value || isTranscriptExpanded.value) return text;
  return `${text.slice(0, TRANSCRIPT_PREVIEW_LENGTH).trimEnd()}…`;
});

const { t } = useI18n();
const audioPlayer = useTemplateRef('audioPlayer');
const {
  isPlaying,
  isMuted,
  currentTime,
  duration,
  playbackSpeedLabel,
  playbackError,
  onLoadedMetadata,
  onTimeUpdate,
  onPlay,
  onPause,
  onError,
  onEnd,
  playOrPause,
  toggleMute,
  seek,
  changePlaybackSpeed,
  downloadAudio,
  formatTime,
} = useAudioPlayback(audioPlayer, () => attachment);
const playLabel = computed(() =>
  isPlaying.value
    ? t('CONVERSATION.AUDIO_PLAYER.PAUSE')
    : t('CONVERSATION.AUDIO_PLAYER.PLAY')
);
const muteLabel = computed(() =>
  isMuted.value
    ? t('CONVERSATION.AUDIO_PLAYER.UNMUTE')
    : t('CONVERSATION.AUDIO_PLAYER.MUTE')
);
</script>

<template>
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
    @play="onPlay"
    @pause="onPause"
    @error="onError"
  >
    <source :src="timeStampURL" />
  </audio>
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div
      class="gap-1 w-full items-center"
      :class="
        compact
          ? 'grid grid-cols-[2rem_minmax(0,1fr)_2rem_1.5rem_1.5rem]'
          : 'flex flex-1 justify-start'
      "
    >
      <button
        type="button"
        class="p-0 border-0 size-8 shrink-0"
        :class="compact ? 'row-start-1 col-start-1' : ''"
        :aria-label="playLabel"
        @click="playOrPause"
      >
        <Icon
          v-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div
        class="tabular-nums whitespace-nowrap text-xs"
        :class="compact ? 'row-start-2 col-start-1 col-span-2 text-start' : ''"
      >
        {{
          t('CONVERSATION.AUDIO_PLAYER.TIME', {
            current: formatTime(currentTime),
            duration: formatTime(duration),
          })
        }}
      </div>
      <div
        class="min-w-0 items-center flex"
        :class="
          compact ? 'row-start-1 col-start-2 col-span-4 py-1' : 'flex-1 px-2'
        "
      >
        <input
          type="range"
          :aria-label="t('CONVERSATION.AUDIO_PLAYER.SEEK')"
          min="0"
          :max="duration"
          :value="currentTime"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        type="button"
        :aria-label="
          t('CONVERSATION.AUDIO_PLAYER.SPEED', { speed: playbackSpeedLabel })
        "
        class="shrink-0 border-0 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        :class="compact ? 'row-start-2 col-start-3 w-8' : 'w-10'"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="shrink-0 p-0 border-0 grid place-content-center"
        :class="compact ? 'row-start-2 col-start-4 size-6' : 'size-8'"
        :aria-label="muteLabel"
        type="button"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="shrink-0 p-0 border-0 grid place-content-center"
        :class="compact ? 'row-start-2 col-start-5 size-6' : 'size-8'"
        :aria-label="t('CONVERSATION.AUDIO_PLAYER.DOWNLOAD')"
        type="button"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <p v-if="playbackError" class="mb-0 text-xs text-n-ruby-11" role="alert">
      {{ t('CONVERSATION.AUDIO_PLAYER.ERROR') }}
    </p>
    <div
      v-if="attachment.transcribedText && showTranscribedText"
      class="text-n-slate-12 p-3 text-sm bg-n-alpha-1 rounded-lg w-full break-words"
    >
      {{ displayedTranscript }}
      <button
        v-if="isTranscriptLong"
        type="button"
        class="block mt-1 p-0 border-0 bg-transparent text-n-slate-11 hover:text-n-slate-12 font-medium"
        @click="isTranscriptExpanded = !isTranscriptExpanded"
      >
        {{
          isTranscriptExpanded
            ? $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_LESS')
            : $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_MORE')
        }}
      </button>
    </div>
  </div>
</template>
